/* Cart Drawer JS — v3.0 (Dawn-Compatible)
   Matches: snippets/cart-drawer.liquid structure
   Opens/closes via .active class on <cart-drawer> and .drawer class
   Uses <cart-drawer-items>, <quantity-input>, <cart-remove-button> sub-elements */

if (!customElements.get('cart-drawer')) {
    class CartDrawer extends HTMLElement {
        constructor() {
            super();
            this._initialized = false;
        }

        connectedCallback() {
            if (this._initialized) return;
            this._initialized = true;
            requestAnimationFrame(() => this.init());
        }

        init() {
            /* Overlay click to close */
            var overlay = this.querySelector('.cart-drawer__overlay, #CartDrawer-Overlay');
            if (overlay) overlay.addEventListener('click', () => this.close());

            /* All close buttons (also handles inline onclick fallback) */
            this.querySelectorAll('.drawer__close').forEach(btn => {
                btn.addEventListener('click', () => this.close());
            });
        }

        open() {
            this.classList.add('active');
            this.style.visibility = 'visible';
            document.body.style.overflow = 'hidden';
            /* Focus the drawer for accessibility */
            var inner = this.querySelector('.drawer__inner');
            if (inner) inner.focus();
            /* Trigger cart icon shake animation */
            var cartIcon = document.querySelector('.header__icon--cart, [href="/cart"], a[href$="/cart"]');
            if (cartIcon) {
                cartIcon.classList.add('shake');
                setTimeout(function () { cartIcon.classList.remove('shake'); }, 700);
            }
        }

        close() {
            this.classList.remove('active');
            document.body.style.overflow = '';
            /* After transition, hide completely */
            setTimeout(() => {
                if (!this.classList.contains('active')) {
                    this.style.visibility = '';
                }
            }, 350);
        }

        /* Called by Dawn's section rendering after cart update */
        renderContents(parsedState) {
            this.getSectionsToRender().forEach((section) => {
                const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                if (elementToReplace && parsedState.sections[section.section]) {
                    elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
                }
            });

            /* Update header cart count */
            if (parsedState.itemCount !== undefined) {
                document.querySelectorAll('.header__cart-count, .mega-drawer__cart-badge').forEach(el => {
                    if (parsedState.itemCount > 0) {
                        el.textContent = parsedState.itemCount;
                        el.style.display = '';
                    } else {
                        el.textContent = '';
                        el.style.display = 'none';
                    }
                });
                /* Trigger badge pulse animation */
                document.querySelectorAll('.cart-count-bubble').forEach(bubble => {
                    bubble.classList.remove('animate');
                    void bubble.offsetWidth; /* force reflow */
                    bubble.classList.add('animate');
                    setTimeout(function () { bubble.classList.remove('animate'); }, 600);
                });
            }
        }

        getSectionInnerHTML(html, selector) {
            return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
        }

        getSectionsToRender() {
            return [
                {
                    id: 'CartDrawer',
                    section: 'cart-drawer',
                    selector: '.drawer__inner'
                }
            ];
        }
    }
    customElements.define('cart-drawer', CartDrawer);
}

/* quantity-input custom element — Dawn compatible */
if (!customElements.get('quantity-input')) {
    class QuantityInput extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.input = this.querySelector('.quantity__input');
            this.changeEvent = new Event('change', { bubbles: true });

            this.querySelectorAll('button').forEach(button => {
                button.addEventListener('click', this.onButtonClick.bind(this));
            });
        }

        onButtonClick(event) {
            event.preventDefault();
            var prev = this.input.value;
            if (event.target.closest('button').name === 'plus') {
                this.input.stepUp();
            } else {
                this.input.stepDown();
            }
            if (prev !== this.input.value) this.input.dispatchEvent(this.changeEvent);
        }
    }
    customElements.define('quantity-input', QuantityInput);
}

/* cart-remove-button custom element */
if (!customElements.get('cart-remove-button')) {
    class CartRemoveButton extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.addEventListener('click', (event) => {
                event.preventDefault();
                var cartDrawer = this.closest('cart-drawer');
                var cartItems = this.closest('cart-drawer-items') || (cartDrawer ? cartDrawer.querySelector('cart-drawer-items') : null);
                if (cartItems) {
                    cartItems.updateQuantity(this.dataset.index, 0);
                }
            });
        }
    }
    customElements.define('cart-remove-button', CartRemoveButton);
}

/* cart-drawer-items custom element — handles AJAX quantity updates */
if (!customElements.get('cart-drawer-items')) {
    class CartDrawerItems extends HTMLElement {
        connectedCallback() {
            this.querySelectorAll('.quantity__input').forEach(input => {
                input.addEventListener('change', this.onQuantityChange.bind(this));
            });
        }

        async onQuantityChange(event) {
            var input = event.target;
            var index = input.dataset.index;
            var quantity = parseInt(input.value);
            await this.updateQuantity(index, quantity);
        }

        async updateQuantity(index, quantity) {
            this.classList.add('updating');
            var body = JSON.stringify({
                line: parseInt(index),
                quantity: quantity,
                sections: ['cart-drawer'],
                sections_url: window.location.pathname
            });

            try {
                var res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: body
                });
                var state = await res.json();

                var cartDrawer = this.closest('cart-drawer');
                if (cartDrawer) {
                    cartDrawer.renderContents({
                        sections: state.sections,
                        itemCount: state.item_count,
                        items: state.items
                    });
                }

                /* Update header cart counts */
                document.querySelectorAll('.header__cart-count, .mega-drawer__cart-badge, [data-cart-count-bubble]').forEach(el => {
                    if (state.item_count > 0) {
                        el.textContent = state.item_count;
                        el.style.display = '';
                    } else {
                        el.textContent = '';
                        el.style.display = 'none';
                    }
                });

                /* If cart is empty, update the drawer state */
                if (state.item_count === 0) {
                    if (cartDrawer) cartDrawer.classList.add('is-empty');
                } else {
                    if (cartDrawer) cartDrawer.classList.remove('is-empty');
                }

            } catch (e) {
                console.error('Cart update error:', e);
            } finally {
                this.classList.remove('updating');
            }
        }
    }
    customElements.define('cart-drawer-items', CartDrawerItems);
}

/* CartItems compatibility class */
class CartItems {
    constructor() { this.items = []; }
    static getSectionsToRender() {
        return [{ id: 'cart-drawer', section: 'cart-drawer', selector: 'cart-drawer' }];
    }
}

/* Open cart drawer on cart:updated event (fired by ATC buttons) */
document.addEventListener('cart:updated', function () {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
        /* Refresh drawer contents first, then open */
        fetch('/?sections=cart-drawer')
            .then(r => r.json())
            .then(data => {
                if (data['cart-drawer']) {
                    var tmp = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
                    var newInner = tmp.querySelector('.drawer__inner');
                    var currentInner = drawer.querySelector('.drawer__inner');
                    if (newInner && currentInner) {
                        currentInner.innerHTML = newInner.innerHTML;
                    }
                    /* Re-init custom elements inside */
                    drawer.querySelector('cart-drawer-items')?.connectedCallback?.();
                }
                drawer.open();
            })
            .catch(() => drawer.open());
    }
});

/* GLOBAL: Intercept all /cart link clicks to open drawer */
document.addEventListener('click', function (e) {
    var link = e.target.closest('a');
    if (!link) return;

    var href = link.getAttribute('href');
    if (!href) return;

    /* Match /cart, /pl/cart, etc. but NOT /cart/add or /cart/change */
    if (href === '/cart' || /^(\/[a-z]{2})?\/cart\/?$/.test(href)) {
        /* Don't intercept links inside the cart drawer itself */
        if (link.closest('cart-drawer')) return;

        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
            e.preventDefault();
            drawer.open();
        }
    }
});

/* Escape key to close */
document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && drawer.classList.contains('active')) {
            drawer.close();
        }
    }
});
